import { trpc } from '@org/clients/school';
import { DynamicForm } from '@org/ui';
import { userUpdateSchema, FEATURE_FLAG_KEYS } from '@org/model';
import { toast } from 'sonner';
import { unwrapTrpcData } from '@org/ui';
import { LazyMedia } from '@org/ui';
import { MediaUploader } from '@org/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@org/ui';
import { Switch } from '@org/ui';
import { z } from 'zod/v4';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LearningPathSelect } from '../learning-path/learning-path-select';

// Points, community level and community role are derived from the student's
// activity in the community — they aren't set by hand here. Omitting them from
// the schema keeps them out of both the rendered form and its validation, so a
// stale value can never block the submit. If we ever need to override them,
// that belongs in a dedicated admin-only form.
// Identity validation, sign-up source and the ASAAS customer id have their own
// step ("Identidade") with proper controls — the generic renderer would show
// them as free text / "[object Object]".
const studentFormSchema = userUpdateSchema.omit({
  points: true,
  communityLevel: true,
  communityRole: true,
  identityStatus: true,
  signupSource: true,
  asaasCustomerId: true,
});

type StudentFormValues = z.infer<typeof studentFormSchema>;
type StudentRendererContext = {
  field: ControllerRenderProps<StudentFormValues>;
  error?: string;
  methods: UseFormReturn<StudentFormValues>;
};

export interface StudentFormRef {
  submit: () => void;
}

export interface StudentFormProps {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  /** Edits vs. the loaded values — lets the multi-step shell warn before leaving the step. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const StudentForm = forwardRef<StudentFormRef, StudentFormProps>(({ defaultValues, onSubmit, onClose, onDirtyChange }, ref) => {
  const utils = trpc.useUtils();
  const userId = defaultValues?.userId;
  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(ref, () => ({
    submit: () => {
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    },
  }));
  const { data: levels } = trpc.levels.list.useQuery();
  const { t } = useTranslation('admin');

  const createMutation = trpc.users.create.useMutation({
    onSuccess: () => utils.users.list.invalidate()
  });

  const updateMutation = trpc.users.update.useMutation({
    onSuccess: () => utils.users.list.invalidate()
  });

const handleSubmit = async (data: any) => {
    let resultData = data;

    if (userId) {
      // For update
      await updateMutation.mutateAsync({data: data, id: userId});
      toast.success(t('common.toasts.updateSuccess', { entity: 'User' }));
      resultData = { ...data, userId };
    } else {
      // For create
      const created = await createMutation.mutateAsync(data);
      toast.success(t('common.toasts.createSuccess', { entity: 'User' }));
      resultData = { ...data, userId: created.userId };
    }
    onSubmit(resultData);
  };
  // Prepare foreign key options for the dynamic form
  const foreignKeyOptions = {
    levels: (unwrapTrpcData(levels) ?? []).map((level: any) => ({
      id: level.levelId,
      label: level.name,
    })),
  };

  // Define custom labels for form fields
  const labelMappings = {
    profileImageUrl: t('students.form.labels.profileImage'),
    enabled: t('students.form.labels.enabled'),
    isAdmin: t('students.form.labels.isAdmin'),
    isTeacher: t('students.form.labels.isTeacher'),
    isStudent: t('students.form.labels.isStudent'),
    firstName: t('students.form.labels.firstName'),
    lastName: t('students.form.labels.lastName'),
    phone: t('students.form.labels.phone'),
    type: t('students.form.labels.type'),
    gender: t('students.form.labels.gender'),
    pathId: t('students.form.labels.learningPath'),
    featureFlags: t('students.form.featureFlags.title'),
  };

  const userTypeOptions = [
    { value: 'group', label: t('students.form.type.group') },
    { value: 'private', label: t('students.form.type.private') },
    { value: 'self-learning', label: t('students.form.type.selfLearning') },
    { value: 'community', label: t('students.form.type.community') },
    { value: 'b2b', label: t('students.form.type.b2b') },
  ] as const;

  const genderOptions = [
    { value: 'female', label: t('students.form.gender.female') },
    { value: 'male', label: t('students.form.gender.male') },
  ] as const;

  // Custom renderer for profileImageUrl using LazyMedia
  const customRenderers: Record<string, (context: StudentRendererContext) => React.ReactNode> = {
    type: ({ field }) => (
      <Select
        value={field.value as string ?? 'group'}
        onValueChange={field.onChange}
      >
        <SelectTrigger className="w-full text-left">
          <SelectValue placeholder={t('students.form.labels.type')} />
        </SelectTrigger>
        <SelectContent>
          {userTypeOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ),
    gender: ({ field }) => (
      <Select
        value={field.value as string ?? 'female'}
        onValueChange={field.onChange}
      >
        <SelectTrigger className="w-full text-left">
          <SelectValue placeholder={t('students.form.labels.gender')} />
        </SelectTrigger>
        <SelectContent>
          {genderOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ),
    // Optional learning path; blank means "follow the default path" (null).
    pathId: ({ field }) => (
      <LearningPathSelect
        value={field.value as string | null | undefined}
        onChange={(pathId) => field.onChange(pathId)}
      />
    ),
    featureFlags: ({ field }) => {
      const flags = (field.value ?? {}) as Record<string, boolean>;
      return (
        <div className="flex flex-col gap-3 rounded-md border p-4">
          <p className="text-xs text-muted-foreground">
            {t('students.form.featureFlags.description')}
          </p>
          {FEATURE_FLAG_KEYS.map((flagKey) => (
            <div key={flagKey} className="flex items-center justify-between gap-4">
              <span className="text-sm">
                {t(`students.form.featureFlags.flags.${flagKey}`)}
              </span>
              <Switch
                checked={flags[flagKey] === true}
                onCheckedChange={(checked) =>
                  field.onChange({ ...flags, [flagKey]: checked })
                }
              />
            </div>
          ))}
        </div>
      );
    },
    profileImageUrl: ({ field }) => {
      const { value, onChange } = field;
      if (typeof value === 'string' && value.length > 0) {
        // Show image preview with LazyMedia
        return (
          <LazyMedia
            title={t('students.form.media.previewTitle')}
            placeholder={t('students.form.media.previewPlaceholder')}
            onReplace={() => onChange('')}
          >
            <div className="aspect-square bg-muted rounded-md overflow-hidden max-w-xs">
              <img
                src={value}
                alt={t('students.form.media.previewAlt')}
                className="object-cover w-full h-full"
              />
            </div>
          </LazyMedia>
        );
      }

      // No image yet - show uploader
      return (
        <MediaUploader
          compact
          acceptedFileTypes={["image/jpeg", "image/png", "image/gif", "image/webp"]}
          title={t('students.form.media.uploadTitle')}
          description={t('students.form.media.uploadDescription')}
          onUploadComplete={(url) => onChange(url)}
        />
      );
    }
  };

  // `language` is the native language (Portuguese by default) and `uiLanguage`
  // is the on-screen choice the student makes in the sidebar toggle.
  const hiddenFields = ['status', 'language', 'uiLanguage'];

  return (
    <DynamicForm
      schema={studentFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      isEditing={!!userId}
      onClose={onClose}
      foreignKeyOptions={foreignKeyOptions}
      labelMappings={labelMappings}
      customRenderers={customRenderers}
      hiddenFields={hiddenFields}
      formRef={formRef}
      onDirtyChange={onDirtyChange}
    />
  );
});
StudentForm.displayName = 'StudentForm'; 